import React, {useState} from 'react';

import {Button, Checkmark, Collapse, DeleteIcon} from '@kit';

import {cls} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import {isSpellReady, SpellListEntry} from '../model/spell-list';
import {ActiveSpellToggle} from './active-spell-toggle';
import {ExpandButton, expandOnClick} from './expandable-row';
import {usageText} from './format';
import {SpellDescription} from './spell-description';
import {SpellTitleIcons} from './spell-title-icons';
import {UsageCount} from './usage-tracker';

import css from './sheet.module.css';

type Props = {
	entry: SpellListEntry;
	/** показывать отметку подготовки и давать её менять */
	withPreparation?: boolean;
};

export const SpellRow: React.FC<Props> = ({entry, withPreparation}) => {
	const {catalog, character, setChoice, update} = useCharacter();
	const [isOpen, setOpen] = useState(false);
	const {spell, preparation, takenByClassId} = entry;
	const takenBy = takenByClassId ? catalog.getClass(takenByClassId)?.title || takenByClassId : undefined;
	const isCantrip = catalog.getSpell(spell.id)?.level === 0;
	const isTracked = !!character.limitedUse(`spell:${spell.id}`);

	const details = [
		spell.alwaysPrepared && 'всегда подготовлено',
		withPreparation && takenBy && `подготовлено: ${takenBy}`,
		!isTracked && usageText(spell.usage, spell.recharge),
		spell.source,
	]
		.filter(Boolean)
		.join(' · ');

	const togglePrepared = () => {
		if (!preparation) return;
		const {choice} = preparation;
		setChoice(
			choice.key,
			choice.selected.includes(spell.id)
				? choice.selected.filter((selectedId) => selectedId !== spell.id)
				: [...choice.selected, spell.id],
		);
	};

	const removeFromBook = () =>
		update((current) => ({
			spellbookSpells: Object.fromEntries(
				Object.entries(current.spellbookSpells || {}).map(([classId, spellIds]) => [
					classId,
					spellIds.filter((spellId) => spellId !== spell.id),
				]),
			),
			...(preparation?.choice.selected.includes(spell.id)
				? {
						choices: {
							...current.choices,
							[preparation.choice.key]: preparation.choice.selected.filter(
								(spellId) => spellId !== spell.id,
							),
						},
				  }
				: null),
		}));

	const renderMark = () => {
		if (isCantrip) return <span className={css.markPlaceholder} />;
		if (!preparation || spell.alwaysPrepared) return <Checkmark checked disabled title="Всегда подготовлено" />;
		const isChecked = preparation.choice.selected.includes(spell.id);
		const isLimitReached =
			preparation.limit !== undefined && preparation.choice.selected.length >= preparation.limit;
		const disabledReason = isChecked
			? undefined
			: takenBy
			? `Уже подготовлено: ${takenBy}`
			: isLimitReached
			? 'Подготовлено максимальное число заклинаний'
			: undefined;
		return (
			<Checkmark
				checked={isChecked}
				disabled={!!disabledReason}
				title={disabledReason}
				aria-label="Подготовить"
				onChange={togglePrepared}
			/>
		);
	};

	return (
		<li className={cls(css.spellRow, withPreparation && !isCantrip && !isSpellReady(entry) && css.notPrepared)}>
			<div className={css.spell}>
				<ExpandButton isOpen={isOpen} onToggle={() => setOpen(!isOpen)} />
				{withPreparation && <span className={css.spellMark}>{renderMark()}</span>}
				<div className={css.spellBody}>
					<span
						className={cls(css.spellName, css.expandable)}
						onClick={expandOnClick(() => setOpen(!isOpen))}
					>
						{catalog.getSpell(spell.id)?.title || spell.id}
						<SpellTitleIcons spellId={spell.id} />
						{details && <span className={css.muted}> {details}</span>}
					</span>
					<span className={css.spellActions}>
						{!withPreparation && <ActiveSpellToggle spellId={spell.id} />}
						<UsageCount useKey={`spell:${spell.id}`} />
						{spell.resourceKey && <UsageCount useKey={spell.resourceKey} />}
						{withPreparation && spell.addedToBook && (
							<Button
								size="small"
								appearance="subtle"
								icon={<DeleteIcon />}
								aria-label="Убрать из книги"
								title="Убрать из книги"
								onClick={removeFromBook}
							/>
						)}
					</span>
				</div>
			</div>
			<Collapse open={isOpen}>
				<SpellDescription spellId={spell.id} />
			</Collapse>
		</li>
	);
};
